/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useEffect, useState } from 'react';
import { AppState, ErrorMessage, KeyValue } from '../../types/common.types';
import { navigate } from 'gatsby';
import { navigation } from '../../middlewares/navigation-constants';
import { Modal, Text, Button, Spacing } from '@dls/web';
import { useDispatch, useSelector } from 'react-redux';
import slugify from '../../helpers/slugify';
import { cartActions, authActions } from '@detox/actions';
import { openLinkInNewTab, isTabAmendedFlow } from '../../helpers/common';
import { OrderSummaryState } from '../../reducers/orderSummary';
import { clearBuyFlowData } from '../../reducers/payment';
import { trans as t } from '../../helpers/localisation';
import { ButtonWrapper } from '../../components/Common';
import { ErrorState as StoreErrorState } from '../../reducers/error';
import CONSTANTS from '../../constants/common';
import { ACTION_TYPES } from '../../constants/actions';
type ErrorState = {
  hasError: boolean;
  errorMessage: string;
  errorTitle: string;
  redirectNeeded?: boolean;
  redirectUrl?: string;
  btnText?: string;
  errorStep?: string;
  errorInfo?: KeyValue;
  extraContactLink?: boolean;
  allowReload?: boolean;
  useErrorFromState?: boolean;
};

export const getErrorInfo = ({
  stateErrors,
  configuredErrors,
  genericTitle,
  genericMessage
}: {
  stateErrors: KeyValue;
  configuredErrors?: ErrorMessage[];
  genericTitle?: string;
  genericMessage: string;
}): KeyValue => {
  const commonProps = {
    redirectNeeded: stateErrors.redirectNeeded ?? true,
    redirectUrl: stateErrors.redirectUrl ?? '',
    btnText: stateErrors.btnText || t('OKAY_GOT_IT'),
    errorStep: stateErrors.errorStep || '',
    extraContactLink: stateErrors.extraContactLink
  };

  if (!stateErrors.errorInfo || !configuredErrors) {
    return {
      errorMessage: stateErrors.errorMessage || genericMessage,
      errorTitle: stateErrors.errorTitle || genericTitle,
      ...commonProps
    };
  }

  const apiErrorCodes = Array.isArray(stateErrors.errorInfo)
    ? stateErrors.errorInfo.map(eInfo => eInfo?.errorCode || '')
    : [stateErrors.errorInfo?.errorCode];
  const filteredApiErrorCodes = apiErrorCodes.filter(Boolean) || [];

  const defaultError = configuredErrors?.find(
    ({ errorCode }) => errorCode === 'GENERIC_ERROR'
  );

  const configuredAPIError =
    configuredErrors?.find(configuredError => {
      return filteredApiErrorCodes?.find(filteredApiErrorCode => {
        return configuredError?.errorCode === filteredApiErrorCode;
      });
    }) || defaultError;

  return {
    errorMessage: configuredAPIError?.description || genericMessage,
    errorTitle: configuredAPIError?.title || genericTitle,
    ...commonProps
  };
};

export const useErrorHandler = ({
  states = [],
  configs,
  callback,
  onCloseErrorPopUp,
  configuredErrors,
  shouldRedirect = true
}: {
  states: AppState[];
  callback?: () => void;
  configs?: {
    isPopup?: boolean;
    enhancedErrorHandler?: boolean;
    suppressError?: boolean;
    reloadOnClose?: boolean;
  };
  onCloseErrorPopUp?: (errorStep: string) => void;
  configuredErrors?: ErrorMessage[];
  shouldRedirect?: boolean;
}): { renderError: () => ReactElement } => {
  const { plan, cart, productOrder, orderSummary, error } = useSelector(
    (state: KeyValue) => {
      return {
        plan: state.plan,
        cart: state.cart,
        productOrder: state.order?.productOrder || {},
        orderSummary: state.orderSummary as OrderSummaryState,
        error: state.error as StoreErrorState
      };
    }
  );

  const aemError = error?.data?.find(
    e => e.errorCode === CONSTANTS.APP_ERROR_CODE
  );

  const {
    isPopup = true,
    enhancedErrorHandler = false,
    suppressError = false,
    reloadOnClose = false
  } = configs || {};
  const dispatch = useDispatch();
  const shouldUseNewPopup = enhancedErrorHandler && aemError;
  const { description, title, footerLinks: buttons } = aemError || {};
  const hasDynamicButtons = buttons?.length > 0;
  const [errorState, setErrorState] = useState<ErrorState>({
    hasError: false,
    errorMessage: '',
    errorTitle: '',
    redirectNeeded: true,
    redirectUrl: '',
    btnText: '',
    errorStep: '',
    extraContactLink: false,
    errorInfo: null,
    allowReload: false
  });
  const genericMessage = shouldUseNewPopup
    ? description
    : (t('GENERIC_ERROR_MESSAGE') as string);
  const genericTitle = shouldUseNewPopup
    ? title
    : (t('GENERIC_ERROR_TITLE') as string);

  useEffect(() => {
    const stateErrors = states.find(
      state => state?.isError || state?.error || state?.hasError
    );

    const concurrentTabErrorRes = states.find(
      state => state?.paymentErrorResponse
    );

    if (stateErrors) {
      const errorInfo = concurrentTabErrorRes?.paymentErrorResponse?.ErrorInfo;
      if (errorInfo && isTabAmendedFlow(errorInfo)) {
        setErrorState({
          hasError: true,
          errorMessage: genericMessage as string,
          errorTitle: genericTitle as string,
          redirectNeeded: true,
          btnText: stateErrors.btnText || t('OKAY_GOT_IT'),
          allowReload: stateErrors.allowReload,
          useErrorFromState: stateErrors.useErrorFromState
        });
      } else {
        const errorInfo = getErrorInfo({
          stateErrors,
          configuredErrors,
          genericTitle,
          genericMessage
        });

        setErrorState({
          hasError: true,
          errorMessage: errorInfo.errorMessage,
          errorTitle: errorInfo.errorTitle,
          redirectNeeded: errorInfo.redirectNeeded,
          redirectUrl: errorInfo.redirectUrl,
          btnText: errorInfo.btnText,
          extraContactLink: errorInfo.extraContactLink,
          errorStep: errorInfo.errorStep,
          allowReload: stateErrors.allowReload,
          useErrorFromState: stateErrors.useErrorFromState
        });
      }
      if (callback) {
        resetErrorState();
        callback();
      } else {
        handleError();
      }
    }
  }, states);

  useEffect(() => {
    const errorInfo = orderSummary?.paymentErrorResponse?.ErrorInfo;

    if (errorInfo && isTabAmendedFlow(errorInfo)) {
      window.localStorage.setItem('OrderInMultiTab', JSON.stringify(errorInfo));
    }
  }, [orderSummary?.paymentErrorResponse]);

  const handleError = () => {
    //todo: might need to add logic to go to error page in case of specific error code.
    if (!isPopup) {
      navigate(`/${navigation.ERROR}`);
      resetErrorState();
    }
  };

  const resetErrorState = () => {
    setErrorState({ hasError: false, errorMessage: '', errorTitle: '' });
    states.forEach(state => {
      state.isError = false;
      state.error = false;
    });
    dispatch({ type: ACTION_TYPES.ERROR.RESET_ERROR_STATE });
  };

  const handleEmptyCart = async productOrder => {
    if (productOrder) {
      await dispatch(
        cartActions.emptyCart({
          customerId: productOrder?.service?.customerId,
          productOrderId: productOrder?.productOrderId,
          productOrderItemId: productOrder?.productOrderItemId
        })
      );
      dispatch({ type: 'CLEAR_DELIVERY' });
    }
  };

  const onCloseErrorPopup = async (isBtnClick = false) => {
    resetErrorState();
    if (reloadOnClose) {
      dispatch({ type: ACTION_TYPES.ERROR.RESET_ERROR_STATE });
      setTimeout(() => {
        return window.location.reload();
      }, 200);
    }
    if (onCloseErrorPopUp) {
      onCloseErrorPopUp(errorState.errorStep);
    }
    if (!errorState.redirectNeeded || !shouldRedirect) {
      return;
    }

    let path = '';
    if (errorState?.redirectUrl && isBtnClick) {
      path = errorState.redirectUrl;
    } else if (errorState?.errorMessage === t('ORDER_AMENDED_IN_OTHER_FLOW')) {
      await handleEmptyCart(productOrder);
      const errorInfo = orderSummary?.paymentErrorResponse?.ErrorInfo;
      window.localStorage.setItem('OrderInMultiTab', JSON.stringify(errorInfo));
      path = `/${navigation.EMPTY_SHOPPING_CART}`;
    } else {
      const hasHandset = cart?.cartOrder?.device;
      path = hasHandset
        ? '/'
        : `/plans/?group=${slugify(plan?.selectedPlan?.groupName)}`;
      dispatch(authActions.deleteWorryFree());
      dispatch(clearBuyFlowData());
    }

    return navigate(path, { replace: true });
  };
  const renderReloadButton = () => {
    if (!errorState.allowReload) return null;
    return (
      <>
        <Button
          data-testid="reload-button"
          primary
          onClick={() => {
            window.location.reload();
          }}
        >
          {t('RELOAD_TEXT')}
        </Button>
      </>
    );
  };

  const renderDynamicButton = (
    buttons: {
      link: string;
      label: string;
    }[]
  ) => {
    return buttons.map(button => {
      return (
        <Button
          data-testid="reload-button"
          primary
          onClick={() => {
            openLinkInNewTab(button.link);
          }}
        >
          {button.label}
        </Button>
      );
    });
  };

  const renderButtons = () => {
    if (hasDynamicButtons && !errorState.useErrorFromState) {
      return (
        <Spacing top={2}>
          <ButtonWrapper>{renderDynamicButton(buttons)}</ButtonWrapper>
        </Spacing>
      );
    }
    return (
      <>
        {errorState.extraContactLink && (
          <Spacing top={2}>
            <ButtonWrapper>
              <Button
                data-testid="open-contact-popup"
                fullWidth
                onClick={() => openLinkInNewTab()}
              >
                {errorState.btnText}
              </Button>
            </ButtonWrapper>
          </Spacing>
        )}

        {!errorState.extraContactLink && (
          <Spacing responsive={false} top={3}>
            <ButtonWrapper>
              <Button
                data-testid="close-error-popup"
                secondary
                onClick={() => onCloseErrorPopup(true)}
              >
                {errorState.btnText}
              </Button>
              {renderReloadButton()}
            </ButtonWrapper>
          </Spacing>
        )}
      </>
    );
  };

  const renderError = () => {
    if (suppressError) return null;
    if (isPopup && errorState.hasError) {
      return (
        <Modal
          visible={true}
          title={errorState.errorTitle}
          onClose={() => onCloseErrorPopup(false)}
          backdropClosable={false}
        >
          <Modal.Content>
            <Text type="body">{errorState.errorMessage}</Text>
            {renderButtons()}
          </Modal.Content>
        </Modal>
      );
    }
    return null;
  };

  return { renderError };
};
